// @owners { team: patients }
import { useEffect, useRef, useState } from 'react';

type VersionResponse = {
  version: string;
};

function isVersionResponse(data: unknown): data is VersionResponse {
  return typeof data === 'object' && data !== null && typeof (data as Record<string, unknown>).version === 'string';
}

export const useVersionPoller = (): { isStale: boolean; detectedVersion: string | undefined } => {
  const [isStale, setIsStale] = useState(false);
  const [detectedVersion, setDetectedVersion] = useState<string | undefined>(undefined);
  const isStaleRef = useRef(false);
  const baselineVersion = useRef(process.env.WEB_VERSION);

  useEffect(() => {
    const version = baselineVersion.current;

    if (!version || version === 'unknown') {
      return;
    }

    const checkVersion = async () => {
      if (isStaleRef.current) return;
      try {
        const response = await fetch('/version.json');
        if (!response.ok) return;

        const data: unknown = await response.json();
        if (!isVersionResponse(data)) return;
        if (data.version !== version) {
          isStaleRef.current = true;
          setIsStale(true);
          setDetectedVersion(data.version);
        }
      } catch {
        // swallow silently — network errors should not affect the user
      }
    };

    // Intentionally no immediate check on mount — we only want to check when
    // the patient returns to a tab they left open, not on the initial page load.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };

    // `document` is absent in node-environment Jest runs; guard so React 19's stricter
    // effect cleanup doesn't crash on `undefined.addEventListener`. Capture the binding
    // into a local so the cleanup uses the same reference even if a test's `afterEach`
    // has since reassigned `global.document`.
    if (typeof document === 'undefined') return undefined;
    const doc = document;

    doc.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      doc.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return { isStale, detectedVersion };
};
